/**
 * The kitty exchange: thirteen cards, discard three.
 *
 * Thirteen cards at a 26 px fan step is below any tap target (FINDINGS), so the
 * spread is two rows at a 50 px step and a size up, not one squeezed fan. The
 * cards that came from the kitty are stamped on the face, because a floating
 * badge above the card collides with its neighbour at any realistic step.
 */
import { useMemo, useState } from 'react'
import type { CardId } from '@cards/shared'
import type { DecisionBodyProps } from '../types'
import { Fan } from '../../table/Fan'
import { SuitText } from '../../table/atoms'
import { useSettings } from '../../settings/settings'
import { readContract, readJokerSuit } from './spec'
import { cardMarksFor, kittyCardsFor, rankingFor, sortHand } from './hand'
import { useCoach } from '../../coach/useCoach'

function discardCount(payload: unknown): number {
  if (typeof payload === 'object' && payload !== null) {
    const p = payload as Record<string, unknown>
    for (const key of ['chooseCards', 'count', 'discard', 'n']) {
      if (typeof p[key] === 'number') return p[key] as number
    }
  }
  return 3
}

export function KittyExchange({ view, pending, onSubmit, busy }: DecisionBodyProps): JSX.Element {
  const [chosen, setChosen] = useState<readonly CardId[]>([])
  const need = discardCount(pending.payload)
  const hand = view.hand
  const [settings] = useSettings()
  const ranking = rankingFor(view)
  // The spread is the hand, in the order the hand band would fan it: thirteen
  // cards across two rows reading the same way as the ten that come back.
  const cards = useMemo(() => {
    const order = sortHand(view.myCards, ranking)
    return settings.handOrder === 'highLeft' ? order.reverse() : order
  }, [view.myCards, ranking, settings.handOrder])
  const kitty = kittyCardsFor(view)
  // Coach mode rings the three it would bury; choosing is still the player's.
  const coach = useCoach(view)
  const advised = coach?.status === 'ready' && coach.advice.action.kind === 'decide'
    ? ((coach.advice.action.decision as { cards?: readonly CardId[] }).cards ?? [])
    : []

  const toggle = (card: CardId) => {
    setChosen((prev) =>
      prev.includes(card)
        ? prev.filter((c) => c !== card)
        : prev.length >= need
          ? prev
          : [...prev, card],
    )
  }

  const split = Math.ceil(cards.length / 2)
  const rows = [cards.slice(0, split), cards.slice(split)]
  const contract = readContract(hand?.contract)
  const trumpWord = contract && contract.spec.kind === 'suit' ? contract.spec.strain : null

  const toFan = (list: readonly CardId[]) =>
    list.map((card) => {
      const marks = cardMarksFor(card, ranking, kitty)
      return {
        card,
        state: (chosen.includes(card) ? 'selected' : 'rest') as 'selected' | 'rest',
        trump: marks.trump,
        stamp: marks.stamp,
        fromKitty: marks.fromKitty,
        coach: advised.includes(card) && !chosen.includes(card),
        onClick: () => toggle(card),
      }
    })

  return (
    <div className="exchange">
      <div className="group">
        <div className="exchange-head">
          <h2>
            Discard {need} to the kitty
          </h2>
          <p>
            <SuitText text={blurb(trumpWord, readJokerSuit(hand?.extra))} />
          </p>
        </div>
        <div className="spread">
          {rows.map((row, i) => (
            <div className="spread-row" key={i}>
              <Fan cards={toFan(row)} pad={2} />
            </div>
          ))}
        </div>
      </div>
      <div className="kitty-actions">
        <div className="count muted" style={{ gap: 8 }}>
          <span style={{ display: 'flex', gap: 4 }}>
            {Array.from({ length: need }, (_, i) => (
              <i key={i} className={i < chosen.length ? 'on' : undefined} />
            ))}
          </span>
          {chosen.length} of {need} chosen
        </div>
        <button
          className="btn btn-primary"
          disabled={chosen.length !== need || busy}
          onClick={() => onSubmit({ kind: pending.kind, cards: chosen })}
        >
          Discard {need}
        </button>
      </div>
    </div>
  )
}

function blurb(strain: string | null, jokerSuit: string | null): string {
  const names: Record<string, string> = {
    S: 'Spades are trumps: J♠ is the right bower, J♣ the left.',
    C: 'Clubs are trumps: J♣ is the right bower, J♠ the left.',
    D: 'Diamonds are trumps: J♦ is the right bower, J♥ the left.',
    H: 'Hearts are trumps: J♥ is the right bower, J♦ the left.',
    NT: 'No trumps: the Joker is the only card above an ace.',
  }
  const head = strain ? (names[strain] ?? '') : 'The Joker is the top card.'
  const joker = jokerSuit ? ` The Joker is nominated to ${jokerSuit}.` : ''
  return `${head} The tagged cards came from the kitty.${joker}`
}
